import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui'
import { Plus } from 'lucide-react'
import { useAgentMemory, useAuth } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { MemoryEntryRow } from './MemoryEntryRow'

interface AgentMemoryPanelProps {
  projectId: string
  agentId: string
  onCreateClick: () => void
}

export function AgentMemoryPanel({ projectId, agentId, onCreateClick }: AgentMemoryPanelProps) {
  const { data: entries, isLoading, isError, refetch } = useAgentMemory(projectId, agentId)
  const { hasPermission } = useAuth(projectId)
  const canManage = hasPermission(Permissions.Project.Agent.Update, projectId)

  const sorted = useMemo(
    () => [...(entries ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [entries]
  )

  return (
    <div className="flex h-[560px] flex-col space-y-3 p-3 border-border rounded-xl bg-card" data-testid="agent-memory-panel">
      <div className="flex shrink-0 justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-bold text-foreground">Stored Memory</h2>
          <Badge variant="secondary">
            {sorted.length} {sorted.length === 1 ? 'entry' : 'entries'}
          </Badge>
        </div>

        {canManage && (
          <Button onClick={onCreateClick}>
            <Plus className="h-4 w-4" />
            New Memory
          </Button>
        )}
      </div>
      <p className="shrink-0 text-xs text-muted-foreground">
        What this agent remembers in the project it runs in. Each entry is one fact or one piece of feedback, appended to the agent's system prompt before a run and updated after.
      </p>
      {isLoading ? (
        <p className="flex-1 py-4 text-center text-sm text-muted-foreground" role="status" aria-live="polite" data-testid="agent-memory-loading">
          Loading memory…
        </p>
      ) : isError ? (
        <div
          role="alert"
          data-testid="agent-memory-error"
          className="flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-destructive"
        >
          <span>Couldn't load this agent's memory.</span>
          <button className="underline" onClick={() => refetch()}>Retry</button>
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex flex-1 items-center justify-center rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-muted-foreground" data-testid="agent-memory-empty">
          No stored memory yet. Memory accumulates after runs when the agent's memory scope is set to project.
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
          {sorted.map(entry => (
            <MemoryEntryRow key={entry.id} projectId={projectId} entry={entry} canManage={canManage} />
          ))}
        </div>
      )}
    </div>
  )
}
